import { t } from '@lingui/macro';
import useJournalEntryDimensions from '../hooks/useJournalEntryDimensions';

export interface FormFieldSizeConstants {
    id: number;
    textId: string;
    width: number;
}

export const formFieldSizeConstants: FormFieldSizeConstants[] = [
    {
        id: 10,
        textId: 'FIELDSIZE_NAME_MINOR',
        width: 40,
    },
    {
        id: 20,
        textId: 'FIELDSIZE_NAME_SMALL',
        width: 80,
    },
    {
        id: 30,
        textId: 'FIELDSIZE_NAME_NORMAL',
        width: 110,
    },
    {
        id: 40,
        textId: 'FIELDSIZE_NAME_LARGE',
        width: 150,
    },
    {
        id: 50,
        textId: 'FIELDSIZE_NAME_EXTRA_LARGE',
        width: 250,
    },
];

export const FormFieldTypeConstants: {
    id: number;
    name: string;
    defaultSizeLocked: boolean;
    defaultSize: FormFieldSizeConstants;
    defaultSkipLocked: boolean;
    defaultSkip: boolean;
}[] = [
    {
        id: 1,
        name: 'NormalTextField',
        defaultSizeLocked: false,
        defaultSize: formFieldSizeConstants.find(ffsc => ffsc.id === 20) as FormFieldSizeConstants,
        defaultSkip: false,
        defaultSkipLocked: false,
    },
    {
        id: 2,
        name: 'DateTime',
        defaultSizeLocked: true,
        defaultSize: formFieldSizeConstants.find(ffsc => ffsc.id === 30) as FormFieldSizeConstants,
        defaultSkip: false,
        defaultSkipLocked: false,
    },
    {
        id: 3,
        name: 'DateTimeLocked',
        defaultSizeLocked: true,
        defaultSize: formFieldSizeConstants.find(ffsc => ffsc.id === 30) as FormFieldSizeConstants,
        defaultSkip: false,
        defaultSkipLocked: false,
    },
];

export enum JournalEntryFieldAccessType {
    Payments = 1,
    Project = 4,
    Dimension = 5,
    Accruals = 8,
    FixedAssets = 9,
    RotDeduction = 11,
    ClientMoneyLedger = 12,
    NewDimensions = 13,
}

export const FormFieldAccesTypeConstants: { id: number; label: string; color: string; columnWidth: number }[] = [
    {
        id: 1,
        label: t`Payments`,
        color: '#FF7C87',
        columnWidth: 2,
    },
    {
        id: 4,
        label: t`Project`,
        color: '#FFDA48',
        columnWidth: 2,
    },
    {
        id: 5,
        label: t`Dimension`,
        color: '#70E674',
        columnWidth: 2,
    },
    {
        id: 13,
        label: t`Dimensions`,
        color: '#70E674',
        columnWidth: 2,
    },
    {
        id: 8,
        label: t`Accruals`,
        color: '#70CEE6',
        columnWidth: 2,
    },
    {
        id: 9,
        label: t`Fixed assets`,
        color: '#D39F5F',
        columnWidth: 2,
    },
    {
        id: 11,
        label: t`ROT/RUT deduction`,
        color: '#A7C8D1',
        columnWidth: 3,
    },
    {
        id: 12,
        label: t`Client money ledger`,
        color: '#faebd7',
        columnWidth: 2,
    },
];

/**
 * Gets field names by text ID. Consolidatest legacy translation and mulit-dimension fields.
 */
export function useFormFieldNames() {
    const { getNameByTextId } = useJournalEntryDimensions();

    function getByTextId(textId: string) {
        const textMapping = getFormFieldLegacyTextKeyMap();
        let fieldName: string | null | undefined = textMapping.find(map => map.key === textId)?.english;
        if (!fieldName) {
            fieldName = getNameByTextId(textId);
        }

        return fieldName;
    }

    return getByTextId;
}

/**
 *
 *    Map old form field translation keys
 *  with text translated using the new translations system.
 */
export const getFormFieldLegacyTextKeyMap = (): { key: string; english: string }[] => [
    {
        key: 'LEDGER_TABORDER_VAT',
        english: t`VAT`,
    },
    {
        key: 'LEDGER_TABORDER_UNIT2',
        english: t`Unit 2`,
    },
    {
        key: 'LEDGER_TABORDER_UNIT1',
        english: t`Unit 1`,
    },
    {
        key: 'LEDGER_TABORDER_TITLE_TABORDERINLEDGER',
        english: t`Tab order in journal`,
    },
    {
        key: 'LEDGER_TABORDER_TEXT',
        english: t`Text`,
    },
    {
        key: 'LEDGER_TABORDER_RECEIVABLESPAYABLES',
        english: t`Debit/Credit`,
    },
    {
        key: 'LEDGER_TABORDER_PROJECT',
        english: t`Project`,
    },
    {
        key: 'LEDGER_TABORDER_POSTTYPE',
        english: t`Type`,
    },
    {
        key: 'LEDGER_TABORDER_POSTFEE',
        english: t`Enter charge`,
    },
    {
        key: 'LEDGER_TABORDER_PAYMENTDATE',
        english: t`Due date/Payment date`,
    },
    {
        key: 'LEDGER_TABORDER_PAYMENTACCOUNT',
        english: t`Cash account`,
    },
    {
        key: 'LEDGER_TABORDER_MODVATACCOUNT',
        english: t`VAT, contra account`,
    },
    {
        key: 'LEDGER_TABORDER_MODACCOUNT',
        english: t`Contra account`,
    },
    {
        key: 'LEDGER_TABORDER_LIABILITY',
        english: t`Client money balance`,
    },
    {
        key: 'LEDGER_TABORDER_INOICENO',
        english: t`Invoice no.`,
    },
    {
        key: 'LEDGER_TABORDER_EXCHANGERATE',
        english: t`Exchange rate`,
    },
    {
        key: 'LEDGER_TABORDER_EMPLOYEENO',
        english: t`Employee no.`,
    },
    {
        key: 'LEDGER_TABORDER_DISTRIBUTIONBUTTON',
        english: t`Distribution button`,
    },
    {
        key: 'LEDGER_TABORDER_DEPARTMENT',
        english: t`Department`,
    },
    {
        key: 'LEDGER_TABORDER_DATE',
        english: t`Date`,
    },
    {
        key: 'LEDGER_TABORDER_CURRENCY',
        english: t`Currency`,
    },
    {
        key: 'LEDGER_TABORDER_CREATEPAYMENT',
        english: t`Payment type`,
    },
    {
        key: 'LEDGER_TABORDER_COUNT2',
        english: t`Quantity 2`,
    },
    {
        key: 'LEDGER_TABORDER_COUNT',
        english: t`Quantity`,
    },
    {
        key: 'LEDGER_TABORDER_CASENO',
        english: t`Project no.`,
    },
    {
        key: 'LEDGER_TABORDER_ATTACHMENTNO',
        english: t`Entry`,
    },
    {
        key: 'LEDGER_TABORDER_AMOUNT',
        english: t`Amount`,
    },
    {
        key: 'LEDGER_TABORDER_ACCRECPAYNO',
        english: t`Account/Customer/Supplier`,
    },
    {
        key: 'LEDGER_TABORDER_ACTIVATION_START',
        english: t`Start date`,
    },
    {
        key: 'LEDGER_TABORDER_ACTIVATION_SLUT',
        english: t`End date`,
    },
    {
        key: 'LEDGER_TABORDER_ACTIVATION_ACCOUNT',
        english: t`Accruals`,
    },
    {
        key: 'LEDGER_TABORDER_ASSET_START_DATE',
        english: t`Start date`,
    },
    {
        key: 'LEDGER_TABORDER_ASSET_NUMBER',
        english: t`Asset no.`,
    },
    {
        key: 'LEDGER_TABORDER_ASSET_LIFE_SPAN',
        english: t`Useful life`,
    },
    {
        key: 'LEDGER_TABORDER_ASSET_GROUP',
        english: t`Asset group`,
    },
    {
        key: 'LEDGER_TABORDER_PAYMENT_ID',
        english: t`Payment ID`,
    },
    {
        key: 'LEDGER_TABORDER_CREDITOR_ID',
        english: t`Supplier ID`,
    },
    {
        key: 'FORM_LEDGER_EDITFIELD_ROTPAYMENT',
        english: t`ROT deduction`,
    },
    {
        key: 'FORM_LEDGER_EDITFIELD_ROTPAYMENTENTRY',
        english: t`ROT deduction entries`,
    },
    {
        key: 'FORM_LEDGER_EDITFIELD_CASENAME',
        english: t`Project name`,
    },
    {
        key: 'FORM_LEDGER_EDITFIELD_EMPNAME',
        english: t`Employee name`,
    },
];
